import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated } from 'react-spring';
import classNames from 'classnames';

import { isPrivateLink } from '../utils/url';

import { Text, Link } from '@brandwatch/axiom-components';
import SlideNavigation from '../containers/SlideNavigation.js';
import { getLogoUrl } from '../logoUrl';
import SlideLogo from './SlideLogo.jsx';
import InfoConsoleButton from './InfoConsole/InfoConsoleButton.jsx';
import getReadStatusKeyName from '../utils/getReadStatusKeyName.js';

import './DeckFooter.css';

const DeckLink = ({ name, canEditFolder, deckEditUrl }) => {
  if (!name) {
    return <Text style={{ visibility: 'hidden' }}>Placeholder</Text>;
  }

  if (!canEditFolder) {
    return (
      <Text textBreak="none" textSize="headline" textStrong>
        {name}
      </Text>
    );
  }
  return (
    <Link
      textBreak="none"
      textSize="headline"
      textStrong
      href={deckEditUrl}
      style="body"
    >
      {name}
    </Link>
  );
};

DeckLink.propTypes = {
  name: PropTypes.node,
  canEditFolder: PropTypes.bool,
  deckEditUrl: PropTypes.string
};

const DeckFooterPercentage = () => {
  const [st, set] = useSpring(() => ({width: '0%'}));
  useEffect(() => {
    const listener = window.addEventListener('scroll', () => {
      const st = document.scrollingElement.scrollTop;
      const ht = document.scrollingElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const percentage = (st / (ht - windowHeight)) * 100;
      set({width: `${percentage}%`});
    });
    return () => window.removeEventListener('scroll', listener);
  });
  return <animated.div className="DeckFooter__percentage" style={st}/>;
};

class DeckFooter extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      hasReadInvite: localStorage.getItem(
        getReadStatusKeyName(this.props.invite)
      )
    };
  }

  render() {
    const {
      invite,
      name,
      slideCount,
      slideIndex,
      toggleInfoConsole,
      canEditFolder,
      folderOptions,
      deckEditUrl
    } = this.props;

    const footerRightClasses = classNames(
      'DeckFooter__right-side',
      'DeckFooter__end',
      {
        // Private links currently don't allow a message to be added to the
        // deck, so no need to display the button for that.
        'DeckFooter__right-side--hidden': isPrivateLink(),
      }
    );

    return (
      <div className="DeckFooter">
        <DeckFooterPercentage />
        <div className="DeckFooter__inner">
          <div className="DeckFooter__left-side DeckFooter__end">
            <SlideLogo src={getLogoUrl(folderOptions)} />
            <div className="DeckName">
              <DeckLink
                name={name}
                canEditFolder={canEditFolder}
                deckEditUrl={deckEditUrl}
              />
            </div>
          </div>
          <SlideNavigation slideCount={slideCount} slideIndex={slideIndex} />
          <div className={footerRightClasses}>
            <InfoConsoleButton
              toggleInfoConsole={toggleInfoConsole}
              invite={invite}
            />
          </div>
        </div>
      </div>
    );
  }
}

DeckFooter.propTypes = {
  invite: PropTypes.object,
  name: PropTypes.string,
  slideCount: PropTypes.number.isRequired,
  slideIndex: PropTypes.number.isRequired,
  toggleInfoConsole: PropTypes.func.isRequired,
  canEditFolder: PropTypes.bool.isRequired,
  deckEditUrl: PropTypes.string.isRequired,
  folderOptions: PropTypes.object.isRequired
};

DeckFooter.defaultProps = {
  folderOptions: {}
};

export default DeckFooter;
